
import { Language } from '../types';

// --- CURRENCY ---
// Fix: Use Record<string, number> to allow additional language keys (ja, zh, es) used in the formatting logic
const EXCHANGE_RATES: Record<string, number> = {
    en: 0.83, ko: 1450, ja: 162, zh: 7.6, es: 1
};

// Fix: Use Record<string, string> to allow additional language keys (ja, zh, es) used in the formatting logic
const CURRENCY_SYMBOLS: Record<string, string> = {
    en: '£', ko: ' ', ja: '¥', zh: '¥', es: '€'
};

export const parseCurrencyString = (str: any): number => {
    if (!str || str === '-' || str === 'Free' || str === '?') return 0;
    const s = String(str);
    if (s.toLowerCase().includes('loan')) return 0;
    
    let clean = s.replace(/[^0-9.kmKM]/g, '').toLowerCase();
    let multiplier = 1;
    if (clean.includes('m')) {
        multiplier = 1000000;
        clean = clean.replace('m', '');
    } else if (clean.includes('k')) {
        multiplier = 1000;
        clean = clean.replace('k', '');
    }
    
    const val = parseFloat(clean);
    return isNaN(val) ? 0 : val * multiplier;
};

export const formatCurrency = (valueStr: any, lang: Language): string => {
    const val = valueStr === null || valueStr === undefined ? '' : String(valueStr);
    
    if (!val || val === '-' || val === '?' || val.toLowerCase().includes('free') || val.toLowerCase().includes('loan')) {
        if (val.toLowerCase().includes('free')) {
            const map: Record<string, string> = { ko: '자유 계약', ja: 'フリー', zh: '免签', es: 'Libre' };
            return map[lang] || 'Free';
        }
        if (val.toLowerCase().includes('loan')) {
            const map: Record<string, string> = { ko: '임대', ja: 'レンタル', zh: '租借', es: 'Cesión' };
            return map[lang] || 'Loan';
        }
        return val || '-';
    }

    const eurValue = parseCurrencyString(val);
    if (eurValue === 0) return val;

    const rate = EXCHANGE_RATES[lang] || 1;
    const localValue = eurValue * rate;
    const symbol = CURRENCY_SYMBOLS[lang] || '€';

    if (['ko', 'ja', 'zh'].includes(lang)) {
        const units = {
            ko: { big: '억', small: '만' },
            ja: { big: '億', small: '万' },
            zh: { big: '亿', small: '万' }
        };
        const u = units[lang as 'ko' | 'ja' | 'zh'];
        
        if (localValue >= 100000000) {
            const major = Math.floor(localValue / 100000000);
            const minor = Math.round((localValue % 100000000) / 10000);
            if (minor === 0) return `${symbol}${major}${u.big}`;
            return `${symbol}${major}${u.big} ${minor}${u.small}`;
        } else if (localValue >= 10000) {
            return `${symbol}${Math.round(localValue / 10000)}${u.small}`;
        }
        return `${symbol}${localValue.toLocaleString()}`;
    }

    if (localValue >= 1000000) {
        return `${symbol}${(localValue / 1000000).toFixed(2).replace(/\.00$/, '')}m`;
    }
    if (localValue >= 1000) {
        return `${symbol}${(localValue / 1000).toFixed(0)}k`;
    }

    return `${symbol}${localValue}`;
};

export const formatMarketValue = (val: any): string => {
    if (val === null || val === undefined) return 'Unknown';
    const sVal = String(val);
    if (sVal.includes('€') || sVal.includes('$') || sVal.includes('£')) return sVal;

    let num = parseFloat(sVal);
    if (isNaN(num)) return 'Unknown';

    if (num >= 1000000) return `€${(num / 1000000).toFixed(2)}m`;
    if (num >= 1000) return `€${(num / 1000).toFixed(1)}k`;
    return `€${num}`;
};

// --- URL & STRING ---
export const cleanUrl = (url: string): string => {
    if (!url) return '';
    let cleaned = url.replace(/([^:]\/)\/+/g, "$1");
    // Standardize to high quality headers (/header/) instead of tiny (/tiny/) or head (/head/)
    return cleaned.replace('/tiny/', '/header/').replace('/head/', '/header/').replace('/medium/', '/header/');
};

/**
 * 팀 ID를 기반으로 최적화된 팀 엠블럼 URL을 생성합니다.
 */
export const getTeamLogo = (teamId: string | number | undefined | null): string => {
    if (!teamId) return '';
    const id = String(teamId).trim();
    if (!id || id === 'undefined' || id === 'null') return '';
    return `https://media.api-sports.io/football/teams/${id}.png`;
};

/**
 * 리그 ID를 기반으로 최적화된 리그 로고 URL을 생성합니다.
 */
export const getLeagueLogo = (leagueId: string | number | undefined | null): string => {
    if (!leagueId) return '';
    const id = String(leagueId).trim();
    if (!id || id === 'undefined' || id === 'null') return '';
    return `https://media.api-sports.io/football/leagues/${id}.png`;
};

// --- DATE & TIME ---
export const calculateTimeAgo = (dateString: string): string => {
    if (!dateString) return 'Recent';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        
        if (diffHrs < 1) return 'Just now';
        if (diffHrs < 24) return `${diffHrs}h ago`;
        
        const days = Math.floor(diffHrs / 24);
        if (days >= 3) {
            return date.toISOString().split('T')[0];
        }
        
        return `${days}d ago`;
    } catch (e) {
        return 'Recent';
    }
};

/**
 * 현지 언어 설정에 맞춰 날짜와 시간을 포맷팅합니다.
 * 한국어('ko')일 경우 KST(Asia/Seoul) 타임존을 적용합니다.
 */
export const formatLocalDateTime = (dateStr: string | null | undefined, lang: Language): string => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (lang === 'ko') {
            return new Intl.DateTimeFormat('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(date);
        }
        return new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(date);
    } catch (e) {
        return dateStr;
    }
};

export const convertTo24Hour = (timeStr: string): string => {
    if (!timeStr) return '';
    const match = timeStr.match(/(\d+):(\d+)\s?(AM|PM)?/i);
    if (!match) return timeStr;

    let [_, h, m, meridiem] = match;
    let hours = parseInt(h, 10);
    
    if (meridiem) {
        if (meridiem.toUpperCase() === 'PM' && hours < 12) hours += 12;
        if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${m}`;
};

export const getEuropeanOffset = (dateStr: string): string => {
    if (!dateStr) return '+01:00';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '+01:00';
    const year = date.getFullYear();
    const march31 = new Date(year, 2, 31);
    const lastSundayMarch = new Date(year, 2, 31 - march31.getDay());
    const oct31 = new Date(year, 9, 31);
    const lastSundayOct = new Date(year, 9, 31 - oct31.getDay());
    if (date >= lastSundayMarch && date < lastSundayOct) return '+02:00';
    return '+01:00';
};
